import { Button, ConfirmDialog, Input, Modal, Select, Surface, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeliveryScheduleCustomRow } from '@warehouse/shared';
import { CalendarPlus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { toastError, toastSuccess } from '../../../lib/notify';
import { ORDER_QUERY_KEYS, deliveryScheduleApi, portalApi } from '../api';

/**
 * "Hanterade datum" — moving a delivery day around a public holiday.
 *
 * The old WMS had this table and rendered it, but there was no INSERT anywhere
 * in the codebase and the server knew nothing about it, so a custom date
 * bypassed the order cutoff entirely (recon §6.1). This panel is the write path,
 * and the server treats a replacement date as inheriting the ORIGINAL date's
 * cutoff — the two halves of the same fix.
 */
export function CustomDatesPanel({
  canManage,
  restaurantId,
}: {
  canManage: boolean;
  restaurantId?: string | undefined;
}): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<DeliveryScheduleCustomRow | null>(null);

  const query = useQuery({
    queryKey: ORDER_QUERY_KEYS.customDates(restaurantId),
    queryFn: () => deliveryScheduleApi.customDates(restaurantId),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['delivery-schedules'] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deliveryScheduleApi.removeCustomDate(id),
    onSuccess: () => {
      toastSuccess(t('salesOrders:schedule.custom.delete.done'));
      setDeleting(null);
      invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const rows = query.data?.items ?? [];

  return (
    <Surface className="p-5" data-testid="custom-dates">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">{t('salesOrders:schedule.custom.title')}</h2>
          <p className="max-w-2xl text-sm text-ink-muted">{t('salesOrders:schedule.custom.subtitle')}</p>
        </div>
        {canManage ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<CalendarPlus className="size-4" aria-hidden />}
            onClick={() => {
              setCreating(true);
            }}
          >
            {t('salesOrders:schedule.custom.actions.new')}
          </Button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('salesOrders:schedule.custom.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-ink-muted">
                <th className="py-2 pr-4">{t('salesOrders:schedule.custom.columns.restaurant')}</th>
                <th className="py-2 pr-4">{t('salesOrders:schedule.custom.columns.changedDate')}</th>
                <th className="py-2 pr-4">{t('salesOrders:schedule.custom.columns.newDate')}</th>
                <th className="py-2 pr-4">{t('salesOrders:schedule.custom.columns.note')}</th>
                <th aria-hidden />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-surface-border/60 last:border-0">
                  <td className="py-2 pr-4">{row.restaurantName}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{row.changedDate}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{row.newDate}</td>
                  <td className="py-2 pr-4 text-ink-muted">{row.note ?? '—'}</td>
                  <td className="py-2 text-right">
                    {canManage ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`${t('salesOrders:schedule.custom.actions.delete')} ${row.changedDate}`}
                        onClick={() => {
                          setDeleting(row);
                        }}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating ? (
        <CustomDateModal
          defaultRestaurantId={restaurantId ?? ''}
          onClose={() => {
            setCreating(false);
          }}
          onCreated={() => {
            setCreating(false);
            invalidate();
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          variant="danger"
          title={t('salesOrders:schedule.custom.delete.title')}
          message={t('salesOrders:schedule.custom.delete.message', { changedDate: deleting.changedDate })}
          confirmLabel={t('salesOrders:schedule.custom.delete.confirm')}
          cancelLabel={t('common:actions.cancel')}
          onConfirm={async () => {
            await deleteMutation.mutateAsync(deleting.id);
          }}
          onClose={() => {
            setDeleting(null);
          }}
        />
      ) : null}
    </Surface>
  );
}

function CustomDateModal({
  defaultRestaurantId,
  onClose,
  onCreated,
}: {
  defaultRestaurantId: string;
  onClose: () => void;
  onCreated: () => void;
}): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const [restaurantId, setRestaurantId] = useState(defaultRestaurantId);
  const [changedDate, setChangedDate] = useState('');
  const [newDate, setNewDate] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const restaurantsQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.restaurants(),
    queryFn: () => portalApi.restaurants(),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      deliveryScheduleApi.createCustomDate({
        restaurantId,
        changedDate,
        newDate,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      toastSuccess(t('salesOrders:schedule.custom.form.created'));
      onCreated();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  function submit(): void {
    if (!restaurantId || !changedDate || !newDate) {
      setError(t('salesOrders:schedule.form.errors.daysRequired'));
      return;
    }
    if (changedDate === newDate) {
      setError(t('salesOrders:schedule.custom.form.sameDate'));
      return;
    }
    setError(null);
    createMutation.mutate(undefined);
  }

  return (
    <Modal open onClose={onClose} title={t('salesOrders:schedule.custom.form.title')} width={520}>
      <div className="flex flex-col gap-4">
        <Select
          label={t('salesOrders:schedule.custom.form.restaurant')}
          placeholder={t('salesOrders:schedule.form.restaurantPlaceholder')}
          value={restaurantId}
          onChange={(event) => {
            setRestaurantId(event.target.value);
          }}
          options={(restaurantsQuery.data?.items ?? []).map((r) => ({ value: r.id, label: r.name }))}
        />
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('salesOrders:schedule.custom.form.changedDate')}</span>
          <Input
            type="date"
            value={changedDate}
            onChange={(event) => {
              setChangedDate(event.target.value);
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('salesOrders:schedule.custom.form.newDate')}</span>
          <Input
            type="date"
            value={newDate}
            onChange={(event) => {
              setNewDate(event.target.value);
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('salesOrders:schedule.custom.form.note')}</span>
          <Textarea
            rows={2}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </label>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={createMutation.isPending} onClick={submit}>
            {t('salesOrders:schedule.custom.form.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
